import type { MockEdge, MockNode } from './types'

/**
 * The toolbar search, in one place so the canvas and the legend agree.
 *
 * A node matches when its label contains the query (case-insensitive), and
 * ONLY the matches are kept — not their ancestors. Searching "dummy" shows
 * the dummy documents and nothing else; the enterprise and site they hang
 * from stay out unless their own names match (in the design's "Texaco" frame
 * they all do, which is why that frame shows the whole tree). The canvas
 * draws this set alone and the legend lists the types in it.
 */
export const normalizeQuery = (query: string | undefined): string => (query ?? '').trim().toLowerCase()

export const labelMatches = (label: string, query: string): boolean => query !== '' && label.toLowerCase().includes(query)

/** Ids of the matching nodes; empty when there is no query. */
export const searchKeepIds = (nodes: MockNode[], rawQuery: string | undefined): Set<string> => {
  const query = normalizeQuery(rawQuery)
  const keep = new Set<string>()
  if (!query) return keep
  for (const n of nodes) if (labelMatches(n.label, query)) keep.add(n.id)
  return keep
}

/**
 * The graph the page draws while searching: the matching nodes and whatever
 * edges run between them. A match whose parent did not match keeps no edge,
 * so the canvas is told to draw unlinked nodes as they are (`keepUnlinked`)
 * rather than treat them as orphans. Returns the input untouched for an empty
 * query, so no search means no rebuild.
 */
export const filterGraphForSearch = <G extends { nodes: MockNode[]; edges: MockEdge[] }>(graph: G, rawQuery: string | undefined): G => {
  if (!normalizeQuery(rawQuery)) return graph
  const keep = searchKeepIds(graph.nodes, rawQuery)
  return {
    ...graph,
    nodes: graph.nodes.filter((n) => keep.has(n.id)),
    edges: graph.edges.filter((e) => keep.has(e.source) && keep.has(e.target)),
  }
}

/** The id of the synthetic orphan `withTestOrphan` adds; a document id prefix it is not. */
export const TEST_ORPHAN_ID = 'orphan:test'
/** The query-string key that asks for it (`?kg_orphan=1`). */
export const TEST_ORPHAN_PARAM = 'kg_orphan'
/** Sample Mapped Tag chips for the test orphan's card, so it reads like the design's. */
export const TEST_ORPHAN_TAGS = ['A214122', 'A214123', 'A214124']

/**
 * Internal testing only: a graph with one extra document node that belongs to
 * nothing and has no edge — an orphan from the start. Every node the agent
 * graph emits hangs off a sheet or the root, so there is no other way to get
 * one on screen without first filtering a parent away. The page adds it for internal
 * users when the URL carries `?kg_orphan=1`; nobody else can reach it.
 *
 * It carries an attached file and (at the page) sample Mapped Tag chips, so
 * its Node Details card has the design's sections to try.
 */
export const withTestOrphan = <G extends { nodes: MockNode[]; edges: MockEdge[] }>(graph: G): G => {
  if (graph.nodes.some((n) => n.id === TEST_ORPHAN_ID)) return graph
  const name = 'Unmapped Standards'
  const orphan: MockNode = {
    id: TEST_ORPHAN_ID,
    label: name,
    type: 'documents',
    attributes: [{ label: 'Name', value: name }],
    attachedDocuments: ['0528-Q69 OIM#'],
  }
  return { ...graph, nodes: [...graph.nodes, orphan] }
}
